import {
  Activity,
  BookOpen,
  CreditCard,
  LayoutDashboard,
  ListTree,
  Receipt,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { paths } from '@app/router/paths';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
}

/**
 * One of the two admin applications this site hosts. They share a login, a
 * shell and a codebase, but each has its own URL space, its own navigation and
 * its own Supabase project behind the API.
 */
export interface AdminApp {
  id: 'budgeting' | 'accounting';
  label: string;
  description: string;
  /**
   * Product icon served from `public/`. These PNGs carry their own colour and
   * rounded corners, so they are rendered as-is rather than tinted or placed on
   * a coloured tile.
   */
  iconSrc: string;
  /**
   * False while an app is not built yet: it still appears in the switcher, so
   * the shape of the product is visible, but it cannot be entered.
   */
  enabled: boolean;
  /** URL prefix that identifies this app. */
  basePath: string;
  /** Where the switcher lands when this app is chosen. */
  homePath: string;
  items: NavItem[];
}

/**
 * The app switcher and the sidebar are both generated from this list. Adding a
 * page means adding a line here and a route in `router/index.tsx`.
 *
 * Order matters: the first entry is the default app.
 */
export const adminApps: AdminApp[] = [
  {
    id: 'budgeting',
    label: 'Budgeting',
    description: 'Films & TV series budgeting',
    iconSrc: '/budgeting_icon.png',
    enabled: true,
    basePath: paths.budgeting.root,
    homePath: paths.budgeting.dashboard,
    items: [
      { label: 'Dashboard', to: paths.budgeting.dashboard, icon: LayoutDashboard },
      { label: 'Users', to: paths.budgeting.users, icon: Users },
      { label: 'Plans', to: paths.budgeting.plans, icon: CreditCard },
      { label: 'Engagement', to: paths.budgeting.engagement, icon: Activity },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    description: 'Cost reports, ledgers and postings',
    iconSrc: '/accounting_icon.png',
    enabled: false,
    basePath: paths.accounting.root,
    homePath: paths.accounting.dashboard,
    items: [
      { label: 'Dashboard', to: paths.accounting.dashboard, icon: LayoutDashboard },
      { label: 'Chart of Accounts', to: paths.accounting.chartOfAccounts, icon: ListTree },
      { label: 'Journals', to: paths.accounting.journals, icon: BookOpen },
      { label: 'Reports', to: paths.accounting.reports, icon: Receipt },
    ],
  },
];

export const DEFAULT_APP: AdminApp = adminApps[0]!;

/**
 * Which app a URL belongs to. Only enabled apps can match: a disabled one has no
 * routes, so a URL under it is not somewhere the shell can render.
 */
export function findAppForPath(pathname: string): AdminApp {
  return (
    adminApps.find(
      (app) =>
        app.enabled && (pathname === app.basePath || pathname.startsWith(`${app.basePath}/`)),
    ) ?? DEFAULT_APP
  );
}
